import { useField } from '@/components/form';
import { ErrorField } from '../error-field';
import { ReactNode } from 'react';
import { InputLabel, RequiredLabel } from './input-label';
import { Checkbox, CheckboxVariant } from './checkbox';
import { TextInput } from './text-input';
import { TextArea } from './text-area';
import { useTranslation } from '@/contexts/i18n-context';
import styles from './checkbox-group.module.css';
import clsx from 'clsx';
import { FieldSet } from './field-set';

type OtherTextInput = 'text-input' | 'text-area';

interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: RequiredLabel;
  readersOnlyLabel?: boolean;
  subLabel?: ReactNode;
  options: { value: string; label: string; key?: string }[];
  name: string;
  className?: string;
  variant?: CheckboxVariant;
  otherName: string;
  otherValue?: string;
  otherEnabled?: boolean;
  otherInput?: OtherTextInput;
  otherPlaceholder?: string;
  otherMaxLength?: number;
  onChange?: () => void;
}

const OTHER_CHECKBOX_NAME = 'Other';

export function CheckboxGroup({
  options,
  name,
  otherName,
  label,
  subLabel,
  readersOnlyLabel,
  variant,
  otherEnabled = false,
  otherValue = '-1',
  otherInput = 'text-input',
  otherPlaceholder,
  otherMaxLength,
  className,
  onChange,
  ...restProps
}: CheckboxProps) {
  const { t } = useTranslation();
  const { fieldProps, getValue, setValue, error } = useField<string[]>(name, restProps);
  const { setValue: setOtherValue } = useField(otherName, {});

  const value = getValue();
  const currentValue = Array.isArray(value) ? value : [];
  const isOtherChecked = currentValue.includes(otherValue);

  const handleOnChange = (currValue: string, checked: boolean) => {
    let newArray: string[];
    if (checked) {
      newArray = currentValue.includes(currValue) ? currentValue : [...currentValue, currValue];
    } else {
      newArray = currentValue.filter((item: string) => item !== currValue);
    }

    setValue(newArray);
    onChange?.();
  };

  const handleOtherCheckboxChange = (checked: boolean) => {
    handleOnChange(otherValue, checked);

    if (!checked) {
      setOtherValue('');
    }
  };

  return (
    <FieldSet className={styles.checkboxGroup}>
      <InputLabel
        label={label}
        subLabel={subLabel}
        required={fieldProps.required}
        as="legend"
        readersOnly={readersOnlyLabel}
      />
      <div className={clsx(variant === 'selector' && styles.selector, className)}>
        {options.map((option) => {
          const key = option.key ?? option.value;

          return (
            <Checkbox
              className={fieldProps.className}
              variant={variant}
              label={option.label}
              name={option.value}
              key={key}
              checked={currentValue.includes(option.value)}
              onChange={(e) => {
                handleOnChange(option.value, e.target.checked);
              }}
            />
          );
        })}
        {otherEnabled && (
          <Checkbox
            className={fieldProps.className}
            label={t('common.other')}
            variant={variant}
            checked={isOtherChecked}
            name={name + '-' + OTHER_CHECKBOX_NAME}
            onChange={(e) => {
              handleOtherCheckboxChange(e.target.checked);
            }}
          />
        )}
      </div>
      {isOtherChecked &&
        (otherInput === 'text-input' ? (
          <TextInput
            name={otherName}
            aria-label={otherPlaceholder ?? t('common.other')}
            placeholder={otherPlaceholder}
            withIconsIndicators={false}
          />
        ) : (
          <TextArea
            name={otherName}
            placeholder={otherPlaceholder}
            rows={3}
            maxLength={otherMaxLength}
          />
        ))}
      <ErrorField error={error} />
    </FieldSet>
  );
}
